import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getOppOrderbookExchanges,
  getOppSpreadsPairs,
  type OppSpreadPairItem,
  type OppSpreadPairsResponse,
  type OppSpreadPairsSortBy,
} from '@/services/oppDetectorApi';
import { hasCategory, renderCategoryLabel, categoryBadgeClass } from '@/lib/instrumentCategory';
import '../OppTop/OppTop.css';

const DEFAULT_LIMIT = 100;
const POLL_MS = 60000;
const DEFAULT_WINDOW_SECS = 300;

const DEFAULT_DEVIATION_PCT = 10;

const WINDOW_OPTIONS = [
  { value: 300, label: '5 分钟' },
  { value: 600, label: '10 分钟' },
  { value: 1800, label: '30 分钟' },
  { value: 3600, label: '1 小时' },
  { value: 14_400, label: '4 小时' },
  { value: 28_800, label: '8 小时' },
  { value: 86_400, label: '24 小时' },
] as const;

const SORT_OPTIONS: Array<{ value: OppSpreadPairsSortBy; label: string }> = [
  { value: 'volatility_std', label: '标准差 (std)' },
  { value: 'volatility_range', label: '振幅 (range)' },
  { value: 'volatility_mad', label: '平均绝对偏差 (MAD)' },
  { value: 'volatility_cv', label: '变异系数 (CV)' },
  { value: 'mid_spread_deviation', label: 'Mid spread 偏移占比' },
];

function formatSpreadPct(spread: number): string {
  if (!Number.isFinite(spread)) return '—';
  return `${(spread * 100).toFixed(4)}%`;
}

function formatRatio(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(2)}%`;
}

function formatScore(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return v.toExponential(3);
}

function formatTsUs(tsUs: number): string {
  if (!Number.isFinite(tsUs) || tsUs <= 0) return '—';
  const ms = Math.floor(tsUs / 1000);
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function toUiExchange(exchange: string): string {
  const normalized = exchange.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'aster') return 'Aster';
  if (normalized === 'edgex') return 'EdgeX';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return exchange;
}

function emptyPage(exchange1: string, exchange2: string): OppSpreadPairsResponse {
  return {
    exchange1,
    exchange2,
    window_secs: DEFAULT_WINDOW_SECS,
    sort_by: 'volatility_std',
    total: 0,
    offset: 0,
    limit: DEFAULT_LIMIT,
    items: [],
  };
}

export function OppSpreadPairs() {
  const [exchanges, setExchanges] = useState<string[]>([]);
  const [exchange1, setExchange1] = useState('');
  const [exchange2, setExchange2] = useState('');
  const [instId1, setInstId1] = useState('');
  const [instId2, setInstId2] = useState('');
  const [windowSecs, setWindowSecs] = useState(DEFAULT_WINDOW_SECS);
  const [sortBy, setSortBy] = useState<OppSpreadPairsSortBy>('volatility_std');
  const [deviationPct, setDeviationPct] = useState(DEFAULT_DEVIATION_PCT);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<OppSpreadPairsResponse>(() => emptyPage('', ''));
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void getOppOrderbookExchanges()
      .then((list) => {
        if (!mounted.current) return;
        setExchanges(list);
        if (list.length >= 2) {
          setExchange1((prev) => prev || list[0]);
          setExchange2((prev) => prev || list[1]);
        }
      })
      .catch((e) => {
        if (!mounted.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!exchange1 || !exchange2 || exchange1 === exchange2) {
      setPage(emptyPage(exchange1, exchange2));
      setLoading(false);
      return;
    }
    try {
      const data = await getOppSpreadsPairs({
        exchange1,
        exchange2,
        inst_id1: instId1.trim() || undefined,
        inst_id2: instId2.trim() || undefined,
        window_secs: windowSecs,
        sort_by: sortBy,
        deviation_pct: sortBy === 'mid_spread_deviation' ? deviationPct : undefined,
        limit,
        offset,
      });
      if (!mounted.current) return;
      setPage(data);
      setError(null);
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [exchange1, exchange2, instId1, instId2, limit, offset, sortBy, windowSecs, deviationPct]);

  useEffect(() => {
    mounted.current = true;
    void load();
    const t = autoRefresh ? window.setInterval(() => void load(), POLL_MS) : null;
    return () => {
      mounted.current = false;
      if (t != null) window.clearInterval(t);
    };
  }, [autoRefresh, load]);

  const resetOffset = useCallback(() => setOffset(0), []);

  const rows = page.items;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(page.total / Math.max(page.limit, 1))),
    [page.limit, page.total],
  );
  const currentPage = useMemo(
    () => Math.floor(page.offset / Math.max(page.limit, 1)) + 1,
    [page.limit, page.offset],
  );

  const jumpToOppSpreadLive = useCallback((item: OppSpreadPairItem) => {
    const params = new URLSearchParams({
      symbol: item.symbol,
      exchange1: toUiExchange(item.exchange1),
      exchange2: toUiExchange(item.exchange2),
      autoQuery: '1',
    });
    const url = `${window.location.origin}/trading?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const canQuery = Boolean(exchange1 && exchange2 && exchange1 !== exchange2);
  const isMidSpreadSort = sortBy === 'mid_spread_deviation';

  return (
    <div className="opp-top-page">
      <header className="opp-top-header">
        <h2>两所价差波动榜</h2>
        <p className="opp-top-desc">
          数据来自 opp-detector <code>/api/opp-detector/spreads/pairs</code>，
          同一 symbol 双向合并，按窗口内波动指标或 mid spread 相对全局 median 的偏移占比排序。
          {autoRefresh ? `每 ${POLL_MS / 1000}s 自动刷新` : '自动刷新已停止'}。
        </p>
      </header>

      <div className="opp-top-toolbar">
        <button type="button" disabled={loading || !canQuery} onClick={() => void load()}>
          立即刷新
        </button>
        <button type="button" onClick={() => setAutoRefresh((v) => !v)}>
          {autoRefresh ? '停止自动刷新' : '启动自动刷新'}
        </button>
        <label className="opp-top-filter-field">
          <span>交易所 1</span>
          <select
            value={exchange1}
            onChange={(e) => {
              setExchange1(e.target.value);
              resetOffset();
            }}
          >
            <option value="">请选择</option>
            {exchanges.map((exchange) => (
              <option key={exchange} value={exchange}>
                {exchange}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>交易所 2</span>
          <select
            value={exchange2}
            onChange={(e) => {
              setExchange2(e.target.value);
              resetOffset();
            }}
          >
            <option value="">请选择</option>
            {exchanges.map((exchange) => (
              <option key={exchange} value={exchange}>
                {exchange}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>inst_id 1（可选）</span>
          <input
            value={instId1}
            onChange={(e) => {
              setInstId1(e.target.value);
              resetOffset();
            }}
            placeholder="exchange1 合约"
          />
        </label>
        <label className="opp-top-filter-field">
          <span>inst_id 2（可选）</span>
          <input
            value={instId2}
            onChange={(e) => {
              setInstId2(e.target.value);
              resetOffset();
            }}
            placeholder="exchange2 合约"
          />
        </label>
        <label className="opp-top-filter-field">
          <span>统计窗口</span>
          <select
            value={windowSecs}
            onChange={(e) => {
              setWindowSecs(Number(e.target.value) || DEFAULT_WINDOW_SECS);
              resetOffset();
            }}
          >
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>排序指标</span>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as OppSpreadPairsSortBy);
              resetOffset();
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {isMidSpreadSort && (
          <label className="opp-top-filter-field">
            <span>偏移阈值 (%)</span>
            <input
              type="number"
              min={0}
              step={0.1}
              value={deviationPct}
              onChange={(e) => {
                const next = Number(e.target.value);
                setDeviationPct(Number.isFinite(next) && next >= 0 ? next : DEFAULT_DEVIATION_PCT);
                resetOffset();
              }}
            />
          </label>
        )}
        <label className="opp-top-filter-field">
          <span>每页条数</span>
          <input
            type="number"
            min={1}
            step={1}
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value);
              setLimit(Number.isFinite(next) && next > 0 ? Math.floor(next) : DEFAULT_LIMIT);
              resetOffset();
            }}
          />
        </label>
        <button
          type="button"
          disabled={loading || offset <= 0}
          onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
        >
          上一页
        </button>
        <button
          type="button"
          disabled={loading || offset + limit >= page.total}
          onClick={() => setOffset((prev) => prev + limit)}
        >
          下一页
        </button>
        <p className="opp-top-meta">
          第 {currentPage} / {totalPages} 页，共 {page.total} 条
        </p>
        {lastOkAt != null && (
          <p className="opp-top-meta">上次成功：{new Date(lastOkAt).toLocaleString('zh-CN', { hour12: false })}</p>
        )}
      </div>

      {!canQuery && (
        <div className="opp-top-error">请选择两个不同的交易所后再查询。</div>
      )}

      {error && <div className="opp-top-error">{error}</div>}

      <div className="opp-top-table-wrap">
        {rows.length === 0 && !loading && !error && canQuery ? (
          <div className="opp-top-empty">暂无数据（服务未启动或窗口内尚无价差样本）</div>
        ) : (
          <table className="opp-top-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Symbol</th>
                <th>交易所对</th>
                <th>inst_id 1</th>
                <th>inst_id 2</th>
                <th className="num">{isMidSpreadSort ? 'Mid spread' : 'Spread'}</th>
                <th className="num">{isMidSpreadSort ? '偏移占比' : '排序分'}</th>
                <th className="num">{isMidSpreadSort ? '偏移/总点' : '样本数'}</th>
                {isMidSpreadSort && <th className="num">全局 median</th>}
                <th>更新时间</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.symbol}-${r.inst_id1}-${r.inst_id2}-${i}`}
                  className="opp-top-row-clickable"
                  onClick={() => jumpToOppSpreadLive(r)}
                  title="点击跳转到 Opp Spread/K 线并自动查询"
                >
                  <td>{page.offset + i + 1}</td>
                  <td>
                    <span className="opp-top-symbol-cell">
                      <span>{r.symbol}</span>
                      {hasCategory(r.category) && (
                        <span className={categoryBadgeClass(r.category)}>{renderCategoryLabel(r.category)}</span>
                      )}
                    </span>
                  </td>
                  <td>
                    {r.exchange1} ↔ {r.exchange2}
                  </td>
                  <td>
                    <span title={r.inst_id1}>{r.inst_id1}</span>
                  </td>
                  <td>
                    <span title={r.inst_id2}>{r.inst_id2}</span>
                  </td>
                  <td className="num">{formatSpreadPct(r.spread)}</td>
                  <td className="num">{isMidSpreadSort ? formatRatio(r.sort_score) : formatScore(r.sort_score)}</td>
                  <td className="num">
                    {isMidSpreadSort
                      ? `${r.deviation_count ?? 0}/${r.sample_count}`
                      : r.sample_count}
                  </td>
                  {isMidSpreadSort && (
                    <td className="num">{formatSpreadPct(r.global_median_mid_spread ?? NaN)}</td>
                  )}
                  <td>{formatTsUs(r.ts_us)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
